import { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../auth/AuthProvider'
import { DATASETS } from '../lib/datasets'
import { loadDatasetCounts } from '../lib/data'
import { supabase } from '../lib/supabase'
import { loadOperationalConfigs, type OperationalConfigRow } from '../lib/operationalConfig'
import type { DatasetKey } from '../types'
import { EmpreendimentosEditor } from './admin/EmpreendimentosEditor'
import { SuiteHelpEditor } from './admin/SuiteHelpEditor'
import { StandardDatasetEditor } from './admin/StandardDatasetEditor'
import { OperationalConfigEditor } from './admin/OperationalConfigEditor'
import { VencimentoGrupoAdmin } from './admin/VencimentoGrupoAdmin'
import { DraftSafetyBar } from './admin/DraftSafetyBar'
import { UsersAdmin } from './admin/UsersAdmin'
import { ApprovalsQueue } from './admin/ApprovalsQueue'
import { GlobalReleasePanel } from './admin/GlobalReleasePanel'
import { ReleaseHistory } from './admin/ReleaseHistory'
import { UsageIntelligencePanel } from './admin/UsageIntelligencePanel'
import { AutoGestaoAdmin } from './admin/AutoGestaoAdmin'
import { VisualTemplateAdmin } from './admin/VisualTemplateAdmin'
import { NovaLimaMatriculasAdmin } from './admin/NovaLimaMatriculasAdmin'
import { FiqueEmDiaAdmin } from './admin/FiqueEmDiaAdmin'

type CountMap = Partial<Record<DatasetKey, { count: number; source: 'online' | 'cache' }>>
type Release = { id: number; version: string; titulo: string; status: 'rascunho' | 'publicado' | 'arquivado'; published_at: string | null }
type StandardDatasetKey = Extract<DatasetKey, 'qualificacoes' | 'acionamentos' | 'servicos' | 'irregularidades'>
type AdminView = 'overview' | 'conteudo' | 'regras' | 'governanca' | 'auto_gestao' | 'usuarios' | 'inteligencia' | 'aparencia' | 'empreendimentos' | 'suite_help' | 'config_tarifas' | 'config_negociacao' | 'config_vencimentos_grupo' | 'nova_lima_matriculas' | 'fique_em_dia_admin' | StandardDatasetKey

const STANDARD_KEYS = new Set<StandardDatasetKey>(['qualificacoes', 'acionamentos', 'servicos', 'irregularidades'])

function currentConfig(rows: OperationalConfigRow[]) {
  const now = new Date()
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
  return [...rows].filter((row) => row.effective_from <= today).sort((a, b) => b.effective_from.localeCompare(a.effective_from))[0] ?? null
}

export function Admin() {
  const { profile } = useAuth()
  const [view, setView] = useState<AdminView>('overview')
  const [counts, setCounts] = useState<CountMap>({})
  const [release, setRelease] = useState<Release | null>(null)
  const [auditCount, setAuditCount] = useState<number | null>(null)
  const [tariffConfigs, setTariffConfigs] = useState<OperationalConfigRow[]>([])
  const [negotiationConfigs, setNegotiationConfigs] = useState<OperationalConfigRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let mounted = true
    Promise.all([
      loadDatasetCounts(),
      supabase.from('releases').select('id,version,titulo,status,published_at').eq('status', 'publicado').order('id', { ascending: false }).limit(1).maybeSingle(),
      supabase.rpc('admin_audit_event_count'),
    ])
      .then(([datasetCounts, releaseResult, auditResult]) => {
        if (!mounted) return
        setCounts(datasetCounts as CountMap)
        if (!releaseResult.error) setRelease(releaseResult.data as Release | null)
        if (!auditResult.error) setAuditCount(Number(auditResult.data ?? 0))
      })
      .catch((err) => mounted && setError(err instanceof Error ? err.message : 'Falha ao carregar painel.'))
      .finally(() => mounted && setLoading(false))

    Promise.all([loadOperationalConfigs('tarifas'), loadOperationalConfigs('negociacao')])
      .then(([tarifas, negociacao]) => {
        if (!mounted) return
        setTariffConfigs(tarifas.rows)
        setNegotiationConfigs(negociacao.rows)
      })
      .catch(() => {
        // O painel permanece disponível mesmo se uma configuração operacional estiver temporariamente indisponível.
      })

    return () => { mounted = false }
  }, [view])

  const activeTariff = useMemo(() => currentConfig(tariffConfigs), [tariffConfigs])
  const activeNegotiation = useMemo(() => currentConfig(negotiationConfigs), [negotiationConfigs])
  const totalRecords = Object.values(counts).reduce((sum, item) => sum + (item?.count ?? 0), 0)
  const autoGestaoActive = view === 'auto_gestao'

  if (profile?.role === 'operador') return <div className="notice error">Seu perfil não possui acesso ao painel administrativo.</div>

  if (view === 'empreendimentos') return <><EmpreendimentosEditor onBack={() => setView('conteudo')} /><DraftSafetyBar /></>
  if (view === 'suite_help') return <><SuiteHelpEditor onBack={() => setView('conteudo')} /><DraftSafetyBar /></>
  if (view === 'config_tarifas') return <><OperationalConfigEditor kind="tarifas" onBack={() => setView('regras')} /><DraftSafetyBar /></>
  if (view === 'config_negociacao') return <><OperationalConfigEditor kind="negociacao" onBack={() => setView('regras')} /><DraftSafetyBar /></>
  if (view === 'config_vencimentos_grupo') return <><VencimentoGrupoAdmin onBack={() => setView('regras')} /><DraftSafetyBar /></>
  if (view === 'nova_lima_matriculas') return <NovaLimaMatriculasAdmin onBack={() => setView('regras')} />
  if (view === 'fique_em_dia_admin') return profile?.role === 'administrador' ? <FiqueEmDiaAdmin onBack={() => setView('regras')} /> : <div className="notice error">Acesso exclusivo do ADMIN.</div>
  if (view === 'auto_gestao') return <section className="workspace-content"><AutoGestaoAdmin onBack={() => setView('overview')} /></section>
  if (STANDARD_KEYS.has(view as StandardDatasetKey)) return <><StandardDatasetEditor datasetKey={view as StandardDatasetKey} onBack={() => setView('conteudo')} /><DraftSafetyBar /></>

  function openDataset(key: DatasetKey) {
    if (key === 'empreendimentos') setView('empreendimentos')
    else if (key === 'suite_help') setView('suite_help')
    else setView(key as StandardDatasetKey)
  }

  return (
    <section className="workspace-content admin-dashboard">
      <div className="page-heading admin-dashboard-heading">
        <div>
          <p className="eyebrow">ADMINISTRAÇÃO</p>
          <h1>Painel do Suite</h1>
          <p className="muted">Governança, conteúdo oficial, configurações operacionais e segurança em áreas separadas.</p>
        </div>
        <span className="status-chip">{profile?.role === 'administrador' ? 'Administrador' : 'Gestão'}</span>
      </div>

      <nav className="admin-section-nav" aria-label="Áreas administrativas">
        <button className={view === 'overview' ? 'active' : ''} onClick={() => setView('overview')}><span>⌂</span><strong>Visão Geral</strong></button>
        <button className={view === 'conteudo' ? 'active' : ''} onClick={() => setView('conteudo')}><span>▦</span><strong>Conteúdo</strong></button>
        <button className={view === 'regras' ? 'active' : ''} onClick={() => setView('regras')}><span>⚙</span><strong>Regras Operacionais</strong></button>
        <button className={autoGestaoActive ? 'active' : ''} onClick={() => setView('auto_gestao')}><span>◒</span><strong>Auto Gestão</strong></button>
        <button className={view === 'governanca' ? 'active' : ''} onClick={() => setView('governanca')}><span>✓</span><strong>Governança</strong></button>
        {profile?.role === 'administrador' && <button className={view === 'aparencia' ? 'active' : ''} onClick={() => setView('aparencia')}><span>◐</span><strong>Aparência</strong></button>}
        {profile?.role === 'administrador' && <button className={view === 'usuarios' ? 'active' : ''} onClick={() => setView('usuarios')}><span>♙</span><strong>Usuários</strong></button>}
        {profile?.role === 'administrador' && <button className={view === 'inteligencia' ? 'active' : ''} onClick={() => setView('inteligencia')}><span>◉</span><strong>Inteligência de Uso</strong></button>}
      </nav>

      {error && <div className="notice error">{error}</div>}
      {loading && <div className="empty-state">Carregando indicadores...</div>}

      {!loading && view === 'overview' && (
        <>
          <div className="metrics-grid admin-overview-metrics">
            <article className="metric-card"><span>Release atual</span><strong>{release?.version ?? '—'}</strong><small>{release?.status ?? 'Sem release'}</small></article>
            <article className="metric-card"><span>Registros oficiais</span><strong>{totalRecords}</strong><small>6 bases estruturadas</small></article>
            <article className="metric-card"><span>Eventos auditados</span><strong>{auditCount ?? '—'}</strong><small>Histórico do banco</small></article>
          </div>

          <section className="admin-section-intro">
            <div><p className="eyebrow">CENTRAL ADMINISTRATIVA</p><h2>Escolha uma área de trabalho</h2><p className="muted">O painel inicial mostra apenas indicadores e acessos. Cada atividade administrativa abre em seu próprio contexto.</p></div>
          </section>

          <div className="admin-area-grid">
            <button type="button" onClick={() => setView('auto_gestao')}><span className="admin-area-icon">◒</span><div><strong>Auto Gestão</strong><small>Importe resultados, gerencie metas e acompanhe a evolução dos atendentes.</small><em>{profile?.role === 'administrador' ? 'Importação e gestão' : 'Consulta da equipe'}</em></div><b>→</b></button>
            <button type="button" onClick={() => setView('conteudo')}><span className="admin-area-icon">▦</span><div><strong>Conteúdo Oficial</strong><small>Gerencie as seis bases publicadas e seus rascunhos.</small><em>{totalRecords} registros</em></div><b>→</b></button>
            <button type="button" onClick={() => setView('regras')}><span className="admin-area-icon">⚙</span><div><strong>Regras Operacionais</strong><small>Tarifas, negociação, vencimentos e bases operacionais específicas.</small><em>4 configurações operacionais</em></div><b>→</b></button>
            <button type="button" onClick={() => setView('governanca')}><span className="admin-area-icon">✓</span><div><strong>Governança</strong><small>Aprovações, publicação, histórico e recuperação de versões.</small><em>Release {release?.version ?? '—'}</em></div><b>→</b></button>
            {profile?.role === 'administrador' && <button type="button" onClick={() => setView('aparencia')}><span className="admin-area-icon">◐</span><div><strong>Aparência da Suite</strong><small>Pré-visualize e publique o template visual usado por toda a operação.</small><em>Original ou Dashboard Azul</em></div><b>→</b></button>}
            {profile?.role === 'administrador' && <button type="button" onClick={() => setView('usuarios')}><span className="admin-area-icon">♙</span><div><strong>Usuários e Acessos</strong><small>Crie contas, altere perfis, status e senhas temporárias.</small><em>Segurança de acesso</em></div><b>→</b></button>}
            {profile?.role === 'administrador' && <button type="button" onClick={() => setView('inteligencia')}><span className="admin-area-icon">◉</span><div><strong>Inteligência de Uso</strong><small>Acompanhe adoção, acessos, módulos e usuários mais ativos.</small><em>Uso do ambiente online</em></div><b>→</b></button>}
          </div>
        </>
      )}

      {!loading && view === 'conteudo' && (
        <section className="admin-section-page">
          <div className="admin-section-title"><div><p className="eyebrow">CONTEÚDO OFICIAL</p><h2>Bases do Suite</h2><p className="muted">Edite individualmente ou em massa. As alterações seguem rascunho, aprovação e publicação global.</p></div><span>{totalRecords} registros publicados</span></div>
          <div className="admin-grid admin-content-grid">
            {DATASETS.map((dataset) => (
              <article className="admin-card is-actionable" key={dataset.key}>
                <div><p className="eyebrow">BASE OFICIAL</p><h2>{dataset.label}</h2><p className="muted">{dataset.description}</p></div>
                <div className="admin-card-footer"><strong>{counts[dataset.key]?.count ?? 0} registros</strong><span>{counts[dataset.key]?.source === 'cache' ? 'cache local' : 'sincronizado'}</span></div>
                <button className="secondary-button full" onClick={() => openDataset(dataset.key)}>Abrir editor</button>
              </article>
            ))}
          </div>
        </section>
      )}

      {!loading && view === 'regras' && (
        <section className="admin-section-page">
          <div className="admin-section-title"><div><p className="eyebrow">REGRAS OPERACIONAIS</p><h2>Configurações por vigência</h2><p className="muted">Cadastre novas vigências sem apagar os valores e regras históricos.</p></div><span>Versionamento controlado</span></div>
          <div className="operational-config-summary admin-rules-grid">
            <article className="operational-config-card">
              <div><h2>Tabela Tarifária</h2><p>Tarifa fixa, faixas, água e esgoto por categoria. Cadastre uma nova vigência sem substituir as anteriores.</p></div>
              <div className="operational-config-card__meta"><strong>{activeTariff?.title ?? 'Não configurada'}</strong><span>{activeTariff ? `vigência ${new Date(`${activeTariff.effective_from}T00:00:00`).toLocaleDateString('pt-BR')}` : 'aguardando instalação'}</span></div>
              <button className="secondary-button full" onClick={() => setView('config_tarifas')}>Gerenciar tarifas</button>
            </article>
            <article className="operational-config-card">
              <div><h2>Regras de Negociação</h2><p>Faixas de atraso, descontos à vista/parcelado e limite de parcelas por vigência.</p></div>
              <div className="operational-config-card__meta"><strong>{activeNegotiation?.title ?? 'Não configurada'}</strong><span>{activeNegotiation ? `vigência ${new Date(`${activeNegotiation.effective_from}T00:00:00`).toLocaleDateString('pt-BR')}` : 'aguardando instalação'}</span></div>
              <button className="secondary-button full" onClick={() => setView('config_negociacao')}>Gerenciar regras</button>
            </article>
            <article className="operational-config-card">
              <div><h2>Vencimentos por Grupo</h2><p>Grupos de Leitura e dias de vencimento permitidos, com alerta fixo de atendimento restrito ao titular.</p></div>
              <div className="operational-config-card__meta"><strong>Regra operacional por grupo</strong><span>vigência e histórico preservados</span></div>
              <button className="secondary-button full" onClick={() => setView('config_vencimentos_grupo')}>Gerenciar vencimentos</button>
            </article>
            <article className="operational-config-card">
              <div><h2>Nova Lima e Região - Matrículas</h2><p>Base vigente de matrículas para orientar cobrança de terra na contratação do esgoto.</p></div>
              <div className="operational-config-card__meta"><strong>Base substituível por Excel</strong><span>consulta online e atualização imediata</span></div>
              <button className="secondary-button full" onClick={() => setView('nova_lima_matriculas')}>Gerenciar matrículas</button>
            </article>
            {profile?.role === 'administrador' && <article className="operational-config-card">
              <div><h2>Fique em Dia 2026 — Base CPF/CNPJ</h2><p>Importação e publicação exclusiva do ADMIN. Gestão e Operadores possuem somente consulta individual.</p></div>
              <div className="operational-config-card__meta"><strong>Base versionada</strong><span>validação de CPF/CNPJ, valores e data de referência</span></div>
              <button className="secondary-button full" onClick={() => setView('fique_em_dia_admin')}>Gerenciar base CPF/CNPJ</button>
            </article>}
          </div>
          <div className="notice info">Bases oficiais e regras operacionais podem fazer parte da mesma release, mas cada conjunto possui seu editor próprio.</div>
        </section>
      )}

      {!loading && view === 'governanca' && (
        <section className="admin-section-page governance-page">
          <div className="admin-section-title"><div><p className="eyebrow">GOVERNANÇA</p><h2>Aprovação, publicação e recuperação</h2><p className="muted">Concentre aqui tudo que altera o estado publicado do Suite.</p></div><span>Release {release?.version ?? '—'}</span></div>
          {profile?.role === 'administrador' && <GlobalReleasePanel />}
          {profile?.role === 'administrador' && <ApprovalsQueue />}
          <ReleaseHistory />
          {profile?.role === 'gestao' && <div className="notice info">A Gestão prepara alterações e acompanha o histórico. Aprovação, publicação e restauração permanecem restritas ao Administrador.</div>}
        </section>
      )}

      {!loading && view === 'aparencia' && profile?.role === 'administrador' && (
        <section className="admin-section-page admin-appearance-page">
          <VisualTemplateAdmin />
        </section>
      )}

      {!loading && view === 'usuarios' && profile?.role === 'administrador' && (
        <section className="admin-section-page admin-users-page">
          <UsersAdmin onBack={() => setView('overview')} />
        </section>
      )}

      {!loading && view === 'inteligencia' && profile?.role === 'administrador' && (
        <section className="admin-section-page admin-usage-page">
          <UsageIntelligencePanel />
        </section>
      )}
    </section>
  )
}
